import { getDb, loadFirestoreSDK } from './firestore';

/**
 * Saves the playback progress for a specific film and user.
 * Throttled saves should be handled by the caller.
 * 
 * @param {string} userId - The user's UID.
 * @param {string} filmId - The film's UID.
 * @param {number} currentTime - Current playback time in seconds.
 * @param {number} duration - Total duration in seconds.
 * @returns {Promise<void>}
 */
export const savePlaybackProgress = async (userId, filmId, currentTime, duration) => {
    if (!userId || !filmId) return;

    try {
        const db = await getDb();
        const { doc, setDoc, serverTimestamp } = await loadFirestoreSDK();

        // Use merge: true to avoid overwriting other potential fields
        const docRef = doc(db, 'users', userId, 'playback', filmId);

        // The original `await` is removed to allow `.then()` and `.catch()`
        setDoc(docRef, {
            currentTime,
            duration,
            lastUpdated: serverTimestamp(),
            watchedAt: serverTimestamp() // Legacy/Auditing support
        }, { merge: true })
            .then(() => {
                console.log('[Firestore] Playback progress saved successfully for user:', userId, 'film:', filmId);
            })
            .catch(err => {
                console.error('[Firestore] Error saving playback progress for user:', userId, 'film:', filmId, 'Error:', err);
            });

    } catch (error) {
        console.error('[Firestore] Unexpected error in savePlaybackProgress:', error);
        // Non-blocking error, user flow continues
    }
};

/**
 * Retrieves the playback progress for a specific film and user.
 * 
 * @param {string} userId 
 * @param {string} filmId 
 * @returns {Promise<{currentTime: number, duration: number}|null>}
 */
export const getPlaybackProgress = async (userId, filmId) => {
    if (!userId || !filmId) return null;

    try {
        const db = await getDb();
        const { doc, getDoc } = await loadFirestoreSDK();

        const docRef = doc(db, 'users', userId, 'playback', filmId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;

    } catch (error) {
        console.warn('Failed to retrieve playback progress:', error);
        return null; // Graceful degradation: treat as first-time viewer
    }
};

/**
 * Retrieves the most recently watched video for the user.
 * 
 * @param {string} userId
 * @returns {Promise<{filmId: string, currentTime: number, duration: number}|null>}
 */
export const getLastWatched = async (userId) => {
    if (!userId) return null;

    try {
        const db = await getDb();
        const { collection, query, orderBy, limit, getDocs } = await loadFirestoreSDK();

        const playbackRef = collection(db, 'users', userId, 'playback');
        const q = query(playbackRef, orderBy('lastUpdated', 'desc'), limit(1));

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
                filmId: doc.id,
                ...doc.data()
            };
        }
        return null;
    } catch (error) {
        console.warn('[Firestore] Failed to retrieve last watched:', error);
        return null;
    }
};
